/* =========================================================================
   useTelemetry — manages workspace/dashboard CRUD and panel widget mutations.
   Uses clean Workspace.panels[] architecture.
   ========================================================================= */

import { useContext, useCallback, useEffect, useMemo } from 'react';
import { CommandCenterStateProviderContext } from '../../../../context';
import useUrlParams from '../../../../hooks/useUrlParams';
import useTrueTwin from '../../../../hooks/useTrueTwin';
import useScenarioDeploymentDetails from '../../../../hooks/useScenarioDeploymentDetails';
import { TelemetryDashboardType, TelemetryPanel, WorkspaceConfig } from '../lib/dashboard-types';
import { getTelemetryStorageKey, sanitizeWorkspaceForBackend, areWorkspacesEqual } from '../lib/telemetry-helpers';
import TelemetryService from '../../../../../../service/TelemetryService';
import { getApiFailureMessage, showToastMessage, uuidv4 } from '../../../../../../utils/commonFunctions';

export const useTelemetry = () => {
  const context = useContext(CommandCenterStateProviderContext);
  if (!context) {
    throw new Error('useTelemetry must be used within a CommandCenterStateProvider');
  }

  const {
    telemetryDashboards,
    setTelemetryDashboards,
    activeTelemetryDashboardId,
    setActiveTelemetryDashboardId,
    expandedTelemetryPanel,
    setExpandedTelemetryPanel,
    configPanel,
    setConfigPanel,
    addModalOpen,
    setAddModalOpen,
    insertGap,
    setInsertGap,
    telemetrySeries,
    setTelemetrySeries,
    telemetryStatus,
    setTelemetryStatus,
    isTelemetryListening,
    setIsTelemetryListening,
    hasUnsavedChanges,
    setHasUnsavedChanges,
    isTelemetryDataLoading,
    setIsTelemetryDataLoading,
    scenarioDeploymentDetails,
    setScenarioDeploymentDetails,
    savedTelemetryDashboards,
    setSavedTelemetryDashboards,
    isSavingTelemetryWorkspace,
    setIsSavingTelemetryWorkspace,
  } = context;

  const { sat_id } = useUrlParams();
  const { truetwin_id } = useTrueTwin();
  const { scenarioDeploymentDetails: hookDeployDetails } = useScenarioDeploymentDetails();

  const deployDetails = scenarioDeploymentDetails || hookDeployDetails;
  const selectedSatellite = deployDetails?.satellites?.find((sat) => sat.satelliteId === sat_id);
  const activeTrueTwinId = selectedSatellite?.trueTwinId || deployDetails?.satellites?.[0]?.trueTwinId || truetwin_id;

  // Control listening state
  const startListening = useCallback(() => setIsTelemetryListening(true), [setIsTelemetryListening]);
  const stopListening = useCallback(() => setIsTelemetryListening(false), [setIsTelemetryListening]);
  const toggleListening = useCallback(() => setIsTelemetryListening((p) => !p), [setIsTelemetryListening]);

  // Active dashboards / workspaces list from context
  const dashboards: TelemetryDashboardType[] = useMemo(
    () => telemetryDashboards?.workspaces || [],
    [telemetryDashboards?.workspaces]
  );

  // Currently active dashboard workspace
  const activeDashboard = useMemo(
    () => dashboards.find((db) => db.id === activeTelemetryDashboardId) || dashboards[0] || null,
    [dashboards, activeTelemetryDashboardId]
  );

  // Active workspace panel widgets
  const activeLayout: TelemetryPanel[] = useMemo(
    () => activeDashboard?.panels || [],
    [activeDashboard?.panels]
  );

  // Save workspace configuration to local state & sessionStorage (marks unsaved changes via deep diff)
  const saveDashboards = useCallback((updated: TelemetryDashboardType[]) => {
    if (!activeTrueTwinId) return;
    const uniqueId = telemetryDashboards?.uniqueId || 'telemetry-config';
    const dashboardType = telemetryDashboards?.dashboardType || 'SIMULATE';
    const config: WorkspaceConfig = {
      uniqueId,
      truetwinId: activeTrueTwinId,
      dashboardType: dashboardType,
      workspaces: updated,
    };
    setTelemetryDashboards(config);

    const storageKey = getTelemetryStorageKey(uniqueId, activeTrueTwinId);
    try {
      sessionStorage.setItem(storageKey, JSON.stringify(updated));
    } catch (e) {
      console.warn('Failed to save telemetry workspaces to sessionStorage', e);
    }
    const isDirty = !areWorkspacesEqual(updated, savedTelemetryDashboards?.workspaces);
    setHasUnsavedChanges(isDirty);
  },
    [activeTrueTwinId, setTelemetryDashboards, telemetryDashboards?.uniqueId, telemetryDashboards?.dashboardType, savedTelemetryDashboards?.workspaces, setHasUnsavedChanges]
  );

  // Persist workspace configuration to backend REST API ONLY when user clicks "Save Workspace"
  const saveWorkspace = useCallback(async () => {
    if (activeTrueTwinId) {
      setIsSavingTelemetryWorkspace(true);
      const sanitizedWorkspaces = sanitizeWorkspaceForBackend(dashboards);
      const payload = {
        dashboardType: telemetryDashboards?.dashboardType || 'SIMULATE',
        workspaces: sanitizedWorkspaces,
      };
      try {
        await TelemetryService.saveDashboardConfig(activeTrueTwinId, payload);
        showToastMessage('success', 'Telemetry workspace configuration saved successfully');
        const uniqueId = telemetryDashboards?.uniqueId || 'telemetry-config';
        const storageKey = getTelemetryStorageKey(uniqueId, activeTrueTwinId);
        try {
          sessionStorage.setItem(storageKey, JSON.stringify(dashboards));
        } catch (_) { }
        setSavedTelemetryDashboards(telemetryDashboards);
        setHasUnsavedChanges(false);
      } catch (err) {
        const msg = getApiFailureMessage(err, 'Update');
        showToastMessage('error', msg ?? 'Failed to save telemetry workspace configuration');
        throw err;
      } finally {
        setIsSavingTelemetryWorkspace(false);
      }
    } else {
      setHasUnsavedChanges(false);
    }
  }, [activeTrueTwinId, dashboards, telemetryDashboards, setSavedTelemetryDashboards, setHasUnsavedChanges, setIsSavingTelemetryWorkspace]);

  // Discard local unsaved changes and revert to last saved state from backend/context
  const discardUnsavedChanges = useCallback(() => {
    if (!activeTrueTwinId) return;
    if (savedTelemetryDashboards) {
      setTelemetryDashboards(savedTelemetryDashboards);
      const uniqueId = savedTelemetryDashboards.uniqueId || 'telemetry-config';
      const storageKey = getTelemetryStorageKey(uniqueId, activeTrueTwinId);
      try {
        if (savedTelemetryDashboards.workspaces && savedTelemetryDashboards.workspaces.length > 0) {
          sessionStorage.setItem(storageKey, JSON.stringify(savedTelemetryDashboards.workspaces));
        } else {
          sessionStorage.removeItem(storageKey);
        }
      } catch (e) {
        console.warn('Failed to revert telemetry workspaces in sessionStorage', e);
      }
    }
    setHasUnsavedChanges(false);
  }, [activeTrueTwinId, savedTelemetryDashboards, setTelemetryDashboards, setHasUnsavedChanges]);

  // Initialize and load workspaces fallback
  useEffect(() => {
    try {
      if (!activeTrueTwinId || (telemetryDashboards && Array.isArray(telemetryDashboards.workspaces) && telemetryDashboards.workspaces.length > 0)) {
        return; // API or previous hydration already set workspaces
      }

      const uniqueId = telemetryDashboards?.uniqueId || 'telemetry-config';
      const storageKey = getTelemetryStorageKey(uniqueId, activeTrueTwinId);
      const stored = sessionStorage.getItem(storageKey);
      const loadedWorkspaces: TelemetryDashboardType[] = stored ? JSON.parse(stored) : [];

      if (Array.isArray(loadedWorkspaces) && loadedWorkspaces.length > 0) {
        setTelemetryDashboards({
          uniqueId,
          truetwinId: activeTrueTwinId,
          dashboardType: telemetryDashboards?.dashboardType || 'SIMULATE',
          workspaces: loadedWorkspaces,
        });

        if (!activeTelemetryDashboardId || !loadedWorkspaces.some((w) => w.id === activeTelemetryDashboardId)) {
          setActiveTelemetryDashboardId(loadedWorkspaces[0].id);
        }
      }
    } catch (e) {
      console.error('Failed to load session telemetry workspaces', e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTrueTwinId]); // Don't include telemetryDashboards — early return guards against overwrite

  // Set panels for active workspace
  const setLayout = useCallback(
    (panels: TelemetryPanel[]) => {
      if (!activeDashboard) return;
      const updated = dashboards.map((db) =>
        db.id === activeDashboard.id ? { ...db, panels } : db
      );
      saveDashboards(updated);
    },
    [dashboards, activeDashboard, saveDashboards]
  );

  // Patch a single panel widget
  const patchWidget = useCallback(
    (id: string, patch: Partial<TelemetryPanel>) => {
      setLayout(activeLayout.map((w) => (w.id === id ? { ...w, ...patch } : w)));
      setExpandedTelemetryPanel((prev) => (prev && prev.id === id ? { ...prev, ...patch } : prev));
      setConfigPanel((prev) => (prev && prev.id === id ? { ...prev, ...patch } : prev));
    },
    [activeLayout, setLayout, setExpandedTelemetryPanel, setConfigPanel]
  );

  // Remove a panel widget
  const removeWidget = useCallback(
    (id: string) => {
      setLayout(activeLayout.filter((w) => w.id !== id));
    },
    [activeLayout, setLayout]
  );

  // Duplicate a panel widget
  const duplicateWidget = useCallback(
    (id: string) => {
      const w = activeLayout.find((o) => o.id === id);
      if (!w) return;
      const maxY = activeLayout.reduce((m, o) => Math.max(m, o.y + o.h), 0);
      setLayout([
        ...activeLayout,
        {
          ...w,
          id: uuidv4(),
          x: 0,
          y: maxY,
          parameters: w.parameters ? w.parameters.map((c) => ({ ...c })) : undefined,
          hiddenCols: w.hiddenCols ? [...w.hiddenCols] : undefined,
        },
      ]);
    },
    [activeLayout, setLayout]
  );

  // Add a new workspace tab
  const addWorkspace = useCallback(() => {
    const newId = `ws-${uuidv4()}`;
    let idx = dashboards.length + 1;
    const names = new Set(dashboards.map((d) => d.name));
    while (names.has(`Workspace ${idx}`)) idx++;
    const updated: TelemetryDashboardType[] = [
      ...dashboards,
      { id: newId, name: `Workspace ${idx}`, panels: [] },
    ];
    saveDashboards(updated);
    setActiveTelemetryDashboardId(newId);
  }, [dashboards, saveDashboards, setActiveTelemetryDashboardId]);

  // Rename a workspace tab
  const renameWorkspace = useCallback(
    (id: string, newName: string) => {
      saveDashboards(dashboards.map((db) => (db.id === id ? { ...db, name: newName } : db)));
    },
    [dashboards, saveDashboards]
  );

  // Duplicate a workspace tab
  const duplicateWorkspace = useCallback(
    (id: string) => {
      const ws = dashboards.find((db) => db.id === id);
      if (!ws) return;
      const newId = `ws-${uuidv4()}`;
      const updated: TelemetryDashboardType[] = [
        ...dashboards,
        {
          id: newId,
          name: `${ws.name} Copy`,
          panels: ws.panels.map((p, i) => ({
            ...p,
            id: uuidv4(),
          })),
        },
      ];
      saveDashboards(updated);
      setActiveTelemetryDashboardId(newId);
    },
    [dashboards, saveDashboards, setActiveTelemetryDashboardId]
  );

  // Delete a workspace tab
  const deleteWorkspace = useCallback(
    (id: string) => {
      if (dashboards.length <= 1) return;
      const remaining = dashboards.filter((db) => db.id !== id);
      saveDashboards(remaining);
      if (activeDashboard?.id === id) {
        setActiveTelemetryDashboardId(remaining[0].id);
      }
    },
    [dashboards, saveDashboards, activeDashboard?.id, setActiveTelemetryDashboardId]
  );

  // Switch active workspace tab
  const switchWorkspace = useCallback(
    (id: string) => {
      setActiveTelemetryDashboardId(id);
    },
    [setActiveTelemetryDashboardId]
  );

  return {
    telemetryDashboards,
    setTelemetryDashboards,
    dashboards,
    activeDashboard,
    activeLayout,
    setLayout,
    patchWidget,
    removeWidget,
    duplicateWidget,
    activeTelemetryDashboardId,
    setActiveTelemetryDashboardId,
    expandedTelemetryPanel,
    setExpandedTelemetryPanel,
    configPanel,
    setConfigPanel,
    addModalOpen,
    setAddModalOpen,
    insertGap,
    setInsertGap,
    telemetrySeries,
    setTelemetrySeries,
    telemetryStatus,
    setTelemetryStatus,
    isTelemetryListening,
    setIsTelemetryListening,
    startListening,
    stopListening,
    toggleListening,
    addWorkspace,
    renameWorkspace,
    duplicateWorkspace,
    deleteWorkspace,
    switchWorkspace,
    hasUnsavedChanges,
    setHasUnsavedChanges,
    isTelemetryDataLoading,
    setIsTelemetryDataLoading,
    scenarioDeploymentDetails,
    setScenarioDeploymentDetails,
    saveWorkspace,
    discardUnsavedChanges,
    isSavingWorkspace: isSavingTelemetryWorkspace,
    savedTelemetryDashboards,
  };
};

export default useTelemetry;
